import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { FiHome, FiBriefcase, FiSettings, FiUsers, FiDatabase, FiMessageSquare, FiChevronDown, FiLinkedin } from "react-icons/fi";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import useStore from "@/services/store";

const translations = {
  en: {
    home: "Home",
    my_ads: "Jobs",
    chat: "Chat",
    organisation: "Settings",
    members: "Members",
    recruiter: "Recruiter",
    pool: "Talent Pool",
    linkedin: "LinkedIn",
  },
  fr: {
    home: "Accueil",
    my_ads: "Offres",
    chat: "Chat",
    organisation: "Paramètres",
    members: "Membres",
    recruiter: "Recruteur",
    pool: "Vivier",
    linkedin: "LinkedIn",
  },
};

const Sidebar = () => {
  const { t } = useComponentTranslation(translations);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user, organization, setOrganization } = useStore();
  const navigate = useNavigate();
  const userOrgs = user?.organisations || [];
  const menuItems = [
    { to: "/recruiter", label: t("home"), icon: FiHome },
    { to: "/recruiter/job-ads", label: t("my_ads"), icon: FiBriefcase },
    { to: "/recruiter/chat", label: t("chat"), icon: FiMessageSquare },
    { to: "/recruiter/pool", label: t("pool"), icon: FiDatabase },
    { to: "/recruiter/members", label: t("members"), icon: FiUsers },
    { to: "/recruiter/linkedin", label: t("linkedin"), icon: FiLinkedin },
    { to: "/recruiter/organisation", label: t("organisation"), icon: FiSettings },
  ];

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white border border-gray-200 shadow-sm text-gray-600 hover:bg-gray-50"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <HiX className="h-6 w-6" /> : <HiMenuAlt3 className="h-6 w-6" />}
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsMobileOpen(false)} />}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 ease-in-out
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <Link to="/recruiter" className="h-16 flex flex-col justify-center px-6 border-b border-gray-200 hover:bg-gray-50 transition-colors">
            <span className="text-xl font-extrabold text-gray-900">Jobego</span>
            <span className="text-xs text-gray-500 font-medium mt-0.5">{t("recruiter")}</span>
          </Link>

          {/* Organisation selector */}
          {userOrgs.length > 1 && (
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="relative">
                <select
                  value={organization?._id || ""}
                  onChange={(e) => {
                    const org = userOrgs.find((o) => o.id === e.target.value);
                    if (org) {
                      setOrganization({ _id: org.id, name: org.name, logo: org.logo });
                      toast.success(`Switched to ${org.name}`);
                      navigate("/recruiter");
                    }
                  }}
                  className="w-full text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 appearance-none cursor-pointer hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#6C4CFF] focus:border-transparent">
                  {userOrgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-3 space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/recruiter"}
                    onClick={() => setIsMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive ? "bg-[#6C4CFF] text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      }`
                    }
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
